import { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type SortingState,
  createColumnHelper,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import { type JournalStats } from "../../../../common/types/index.js";
import styles from "./JournalTable.module.css";

const noDataTextIfNullElseOriginalValue = (value: unknown) => {
  return value === null ? "-" : value;
};

const undefinedIfNullElseOriginalValue = (value: unknown) => {
  return value === null ? undefined : value;
};

const columnHelper = createColumnHelper<JournalStats>();

const columns = [
  columnHelper.accessor("journalFullName", {
    header: "Journal Name",
    cell: (props) => {
      return noDataTextIfNullElseOriginalValue(
        props.row.original.journalFullName
      );
    },
    sortUndefined: "last",
  }),
  columnHelper.accessor(
    (row) => undefinedIfNullElseOriginalValue(row.distinctManuscripts),
    {
      header: "Sample Size",
      cell: (props) => {
        return noDataTextIfNullElseOriginalValue(
          props.row.original.distinctManuscripts
        );
      },
      sortUndefined: "last",
    }
  ),
  columnHelper.accessor(
    (row) => undefinedIfNullElseOriginalValue(row.avgDaysToFirstDecision),
    {
      header: "Avg Initial Submission Review Time (days)",
      cell: (props) => {
        return noDataTextIfNullElseOriginalValue(
          props.row.original.avgDaysToFirstDecision
        );
      },
      sortUndefined: "last",
    }
  ),
  columnHelper.accessor(
    (row) => undefinedIfNullElseOriginalValue(row.stdDaysToFirstDecision),
    {
      header: "Std Dev Initial Submission Review Time (days)",
      cell: (props) => {
        return noDataTextIfNullElseOriginalValue(
          props.row.original.stdDaysToFirstDecision
        );
      },
      sortUndefined: "last",
    }
  ),
  columnHelper.accessor(
    (row) =>
      undefinedIfNullElseOriginalValue(row.pctReceivedRevisionOnInitialSub),
    {
      header: "% needing at least one revision",
      cell: (props) => {
        return noDataTextIfNullElseOriginalValue(
          props.row.original.pctReceivedRevisionOnInitialSub
        );
      },
      sortUndefined: "last",
    }
  ),
  columnHelper.accessor(
    (row) => undefinedIfNullElseOriginalValue(row.pctAcceptedAfterRevision),
    {
      header: "% accepted after revisions",
      cell: (props) => {
        return noDataTextIfNullElseOriginalValue(
          props.row.original.pctAcceptedAfterRevision
        );
      },
      sortUndefined: "last",
    }
  ),
  columnHelper.accessor(
    (row) => undefinedIfNullElseOriginalValue(row.academicField),
    {
      header: "Academic Field",
      cell: (props) => {
        return noDataTextIfNullElseOriginalValue(
          props.row.original.academicField
        );
      },
      sortUndefined: "last",
    }
  ),
];

const JournalTable = () => {
  // Initially sorted by sample size in descending order
  // so users can see the most accurate stats first
  const [sorting, setSorting] = useState<SortingState>([
    { id: "Sample Size", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filtering, setFiltering] = useState("");
  const [journals, setJournals] = useState<JournalStats[]>([]);

  useEffect(() => {
    fetch("/api/journals")
      .then((result) => {
        void result.json().then((data: JournalStats[]) => {
          setJournals(data);
        });
      })
      .catch((error: unknown) => {
        console.error(error);
      });
  }, []);

  const sortedFieldList = useMemo(
    () =>
      journals
        .map((j) => j.academicField)
        // remove duplicate fields
        .filter((item, i, ar) => ar.indexOf(item) === i)
        // remove nulls
        .filter((f) => f !== null)
        // sort into alphabetical order
        // to make looking for specific field easier for user
        .sort(),
    [journals]
  );

  const table = useReactTable({
    columns,
    data: journals,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
      columnFilters: columnFilters,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
    onColumnFiltersChange: setColumnFilters,
  });

  return (
    <div className={styles["journal-stats-table"]}>
      <div>
        <label htmlFor="field-select">Filter by field:</label>{" "}
        <select
          name="fields"
          id="field-select"
          onChange={(e) => {
            setColumnFilters([
              {
                id: "Academic Field",
                value: e.target.value,
              },
            ]);
          }}
        >
          <option value="">All</option>
          {sortedFieldList.map((field: string, index: number) => {
            return (
              <option key={field} value={field}>
                {field}
              </option>
            );
          })}
        </select>
      </div>
      <input
        className={styles["search-bar"]}
        type="text"
        placeholder="Search for a journal"
        value={filtering}
        onChange={(e) => {
          setFiltering(e.target.value);
        }}
      />{" "}
      <div role="toolbar" aria-label="Button menu">
        <div
          className={styles["button-group"]}
          role="group"
          aria-label="Button group"
        >
          <button
            type="button"
            disabled={!table.getCanPreviousPage()}
            onClick={() => {
              table.setPageIndex(0);
            }}
          >
            First Page
          </button>
          <button
            type="button"
            disabled={!table.getCanPreviousPage()}
            onClick={() => {
              table.previousPage();
            }}
          >
            Previous Page
          </button>
          <button
            type="button"
            disabled={!table.getCanNextPage()}
            onClick={() => {
              table.nextPage();
            }}
          >
            Next Page
          </button>
          <button
            type="button"
            disabled={!table.getCanNextPage()}
            onClick={() => {
              table.setPageIndex(table.getPageCount() - 1);
            }}
          >
            Last Page
          </button>
        </div>
      </div>
      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  {(() => {
                    const sortDirection = header.column.getIsSorted();
                    if (sortDirection) {
                      return sortDirection === "asc" ? " 🔼" : " 🔽";
                    }

                    return null;
                  })()}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default JournalTable;
